import BaseDateText from '@/components/base/BaseDateText';
import BaseEllipsisText from '@/components/base/BaseEllipsisText';
import BaseTag, { type BaseTagProps } from '@/components/base/BaseTag';
import type { CSSProperties, ReactNode } from 'react';

interface ValueOption {
  label: string;
  color?: BaseTagProps['color'];
  tooltip?: string;
}

export interface BaseFieldValueProps {
  value: unknown;
  valueType?: 'text' | 'code' | 'number' | 'tag' | 'date' | 'dateTime';
  format?: string;
  timeZone?: string;
  valueEnum?: Record<string, ValueOption>;
  valueClassName?: string;
  valueSuffix?: ReactNode;
  valueStrong?: boolean;
  fontSize?: CSSProperties['fontSize'];
}

/** 列表和详情共用字段展示规则，不内置业务枚举或日期兜底值。 */
function renderValue({
  value,
  valueType = 'text',
  format,
  timeZone,
  valueEnum,
  valueClassName,
  valueSuffix,
  valueStrong,
}: BaseFieldValueProps) {
  if (value === null || value === undefined) return null;
  const text = String(value);
  if (valueType === 'date' || valueType === 'dateTime') {
    return (
      <BaseDateText
        value={value}
        type={valueType}
        format={format}
        timeZone={timeZone}
      />
    );
  }
  if (valueType === 'tag') {
    const option = valueEnum?.[text];
    // 暴露未配置的原值，不猜测业务状态。
    if (!option) return <BaseEllipsisText title={text} />;
    return (
      <BaseTag color={option.color} tooltip={option.tooltip}>
        {option.label}
      </BaseTag>
    );
  }
  if (valueType === 'number') {
    return (
      <span className={`field-value-number text-text ${valueClassName ?? ''}`}>
        {valueStrong ? <strong>{text}</strong> : text}
        {valueSuffix !== undefined && <> {valueSuffix}</>}
      </span>
    );
  }
  const content = <BaseEllipsisText title={text} />;
  if (valueType === 'code')
    return (
      <code
        className={`field-value-code font-code text-[12px] ${
          valueClassName ?? ''
        }`}
      >
        {content}
      </code>
    );
  return (
    <span className={valueClassName}>
      {valueStrong ? <strong>{content}</strong> : content}
    </span>
  );
}

/** 显式字号统一作用于各种类型，未配置时保留各基础组件的默认字号。 */
export default function BaseFieldValue(props: BaseFieldValueProps) {
  const content = renderValue(props);
  if (content === null || props.fontSize === undefined) return content;
  return (
    <span
      className="field-value-font block min-w-0 leading-[1.6]"
      style={{ fontSize: props.fontSize }}
    >
      {content}
    </span>
  );
}
